import { toast } from "sonner";
import { Fragment, useEffect, useRef, useState } from "react";
import { Drawer } from "vaul";

import { createFileRoute } from "@tanstack/react-router";
import { QueryStatus, useMutation } from "@tanstack/react-query";
import { Link as LinkIcon, PencilSimpleLine, Plus, Check, X, CloudArrowUp, ArrowClockwise } from "@phosphor-icons/react";
import { PopoverClose } from "@radix-ui/react-popover";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/layout/heading";
import { ResponsiveMascot } from "@/components/animations/mascot";
import { BrandVoiceModal } from "@/components/modals/brand-voice";
import { FillComponent, ColorValue } from "@/components/input/fill";
import { Card, CardHeader, CardTitle, CardAction, CardContent } from "@/components/ui/card";

import { useSession } from "@/hooks/use-session";
import { useAuthenticationStore } from "@/store/authentication";

import { UploadApiFactory } from "@/apis/upload";
import { ProfileApiFactory } from "@/apis/profile";
import { CreateResearchBrandGuidelinesParams, KnowledgeApiFactory } from "@/apis/knowledge";

import { mBrandGuidelinesFileFormats, mImageExtensions, mImageFormats } from "@/constants/media-assets";
import { unwrapApiError } from "@/lib/error";
import { cn, normalizeWebsite } from "@/lib/utils";

import type { BrandColor, BrandDNA, BrandTypography, BrandVoice } from "@/schema/knowledge/guidelines";
import type { CompanyProfile } from "@/schema/profile";

export const Route = createFileRoute("/dashboard/_layout/brand")({
  component: RouteComponent,
});

const mDrawerStyle = { "--initial-transform": "calc(100% + 8px)" } as React.CSSProperties;
const mContentStyle = { minHeight: "calc(100dvh - 18rem)" } as React.CSSProperties;

interface CompanyProfileProps {
  status: QueryStatus | "idle";
  onUpdateCompanyProfile: (data: Partial<CompanyProfile>) => void;
}

function RouteComponent() {
  const {
    profile: { company },
  } = useSession();

  const input$ = useRef<HTMLInputElement>(null);
  const updateCompanyProfileState = useAuthenticationStore((store) => store.updateCompanyProfile);

  const { mutateAsync: uploadBrandGuidelines, status } = useMutation({
    mutationKey: KnowledgeApiFactory.Keys.UploadBrandGuidelines(),
    mutationFn: (file: File) => KnowledgeApiFactory.Apis.UploadBrandGuidelines(file),
  });

  const handleBrandGuidelinesClick = () => {
    input$.current?.click();
  };

  const handleUploadBrandGuidelines = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!mBrandGuidelinesFileFormats.includes(file.type)) {
      toast.error("You have uploaded an invalid file", { description: "Please upload a PDF, Markdown, Text, or DOCX file" });
      event.target.value = "";
      return;
    }

    try {
      const response = await uploadBrandGuidelines(file);
      updateCompanyProfileState(response.data);
      toast.success("Brand guidelines uploaded and processed successfully!");
    } catch (error) {
      toast.error(unwrapApiError(error));
    } finally {
      event.target.value = "";
    }
  };

  return (
    <div className="min-h-full w-full @container">
      <PageHeader title="Brand DNA" description="Here is a snapshot of your brand guidelines that we'll for your creatives" className="mb-8">
        <Button
          variant="default"
          size="default"
          onClick={handleBrandGuidelinesClick}
          disabled={status === "pending"}
          className={cn("gap-2", !company.brand_guidelines || company.brand_research_status === "partial" ? "hidden" : "")}
        >
          {status === "pending" ? <div className="spinner size-4" /> : <CloudArrowUp className="size-5" />}
          {status === "pending" ? "Uploading..." : "Upload Brand DNA"}
          <input
            ref={input$}
            type="file"
            accept=".pdf,.md,.markdown,.txt,.docx,application/pdf,text/markdown,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={handleUploadBrandGuidelines}
            className="hidden"
          />
        </Button>
      </PageHeader>
      <BrandDNA onUploadBrandGuidelines={handleBrandGuidelinesClick} uploadBrandGuidelinesStatus={status} />
    </div>
  );
}

interface BrandDNAProps {
  onUploadBrandGuidelines: () => void;
  uploadBrandGuidelinesStatus: QueryStatus | "idle";
}

function BrandDNA({ onUploadBrandGuidelines, uploadBrandGuidelinesStatus }: BrandDNAProps) {
  const {
    profile: { company },
  } = useSession();

  const updateCompanyProfileState = useAuthenticationStore((store) => store.updateCompanyProfile);
  const [isCompanyWebsitePopoverOpen, setCompanyWebsitePopoverOpen] = useState(false);
  const [companyWebsite, setCompanyWebsite] = useState(company.company_website || "");

  const { mutateAsync: createBrandGuidelinesResearch, status: createBrandGuidelinesResearchStatus } = useMutation({
    mutationKey: KnowledgeApiFactory.Keys.CreateBrandGuidelinesResearch(),
    mutationFn: (body: CreateResearchBrandGuidelinesParams) => KnowledgeApiFactory.Apis.CreateBrandGuidelinesResearch(body),
  });

  const { mutateAsync: updateCompanyProfile, status: updateCompanyProfileStatus } = useMutation({
    mutationFn: (data: Partial<CompanyProfile>) => ProfileApiFactory.Apis.UpdateCompanyProfile(data),
  });

  useEffect(() => {
    if (!isCompanyWebsitePopoverOpen) return;
    setCompanyWebsite(company.company_website || "");
  }, [company.company_website, isCompanyWebsitePopoverOpen]);

  const handleCreateBrandGuidelinesResearch = async () => {
    if (!company.company_name || !company.company_website) {
      toast.error("Your brand details are required to retry the brand research");
      return;
    }
    try {
      updateCompanyProfileState({ brand_research_status: "pending" });
      await createBrandGuidelinesResearch({ brand_name: company.company_name!, brand_url: company.company_website! });
      updateCompanyProfileState({ brand_research_status: "completed" });
    } catch (error) {
      toast.error(unwrapApiError(error));
    }
  };

  const handleUpdateCompanyProfile = async (data: Partial<CompanyProfile>) => {
    const updated = await updateCompanyProfile(data);
    updateCompanyProfileState(updated.data);
  };

  const handleUpdateCompanyWebsite = async () => {
    try {
      const website = normalizeWebsite(companyWebsite);
      if (website === company.company_website) {
        setCompanyWebsitePopoverOpen(false);
        return;
      }

      await handleUpdateCompanyProfile({ company_website: website });
      toast.success("Website updated successfully!", { description: "We'll use your new website to create your brand guidelines." });
      setCompanyWebsitePopoverOpen(false);
      handleCreateBrandGuidelinesResearch();
    } catch (error) {
      toast.error("Failed to update website", { description: unwrapApiError(error) });
    }
  };

  if (!company.brand_guidelines) {
    if (company.brand_research_status === "pending") {
      return (
        <div className="flex flex-col items-center justify-center h-full w-full text-center" style={mContentStyle}>
          <ResponsiveMascot mascot="MascotLoading" className="size-72 md:size-96 relative mx-auto -top-2 -right-2.5 md:right-3.5" />
          <p className="text-lg font-medium text-foreground -mt-10 mb-1">Your brand research is still in progress</p>
          <p className="text-sm text-muted-foreground mt-1">Please wait for the research to complete and try again. We will notify you when it's ready.</p>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center h-full w-full text-center" style={mContentStyle}>
        <ResponsiveMascot mascot="MascotError" className="size-64 md:size-96 relative mx-auto -top-2 -right-2.5 md:right-3.5" />
        <p className="text-lg font-medium text-foreground -mt-10 mb-1">We were unable to fetch your brand guidelines</p>
        <p className="text-sm text-muted-foreground mt-1">You can upload your own brand guidelines to get started, or retry the brand research.</p>
        <div className="flex flex-col items-center gap-2 mt-6 w-full sm:w-auto">
          <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-2">
            <Button
              size="default"
              variant="outline"
              className="gap-2"
              onClick={handleCreateBrandGuidelinesResearch}
              disabled={uploadBrandGuidelinesStatus === "pending" || createBrandGuidelinesResearchStatus === "pending"}
            >
              {createBrandGuidelinesResearchStatus === "pending" ? <div className="spinner size-4" /> : <ArrowClockwise className="size-5" />}
              {createBrandGuidelinesResearchStatus === "pending" ? "Retrying..." : "Retry Brand Research"}
            </Button>
            <Popover open={isCompanyWebsitePopoverOpen} onOpenChange={setCompanyWebsitePopoverOpen}>
              <PopoverTrigger asChild>
                <Button variant="secondary" size="default" disabled={uploadBrandGuidelinesStatus === "pending" || updateCompanyProfileStatus === "pending"} className="gap-2">
                  {updateCompanyProfileStatus === "pending" ? <div className="spinner size-4" /> : <PencilSimpleLine className="size-5" />}
                  {updateCompanyProfileStatus === "pending" ? "Updating..." : "Change brand website"}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="center" side="top" sideOffset={8} className="w-[calc(100vw-1.5rem)] sm:w-96">
                <p className="text-sm font-medium text-foreground">Update your website</p>
                <p className="text-xs text-muted-foreground mt-1">We’ll use this to retry your brand research.</p>
                <div className="mt-4 space-y-3">
                  <Input
                    autoFocus
                    value={companyWebsite}
                    placeholder="https://example.com"
                    onChange={(e) => setCompanyWebsite(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleUpdateCompanyWebsite();
                      }
                    }}
                    disabled={updateCompanyProfileStatus === "pending"}
                  />
                  <div className="flex items-center justify-end gap-2">
                    <PopoverClose asChild>
                      <Button variant="ghost" size="sm" disabled={updateCompanyProfileStatus === "pending"}>
                        Cancel
                      </Button>
                    </PopoverClose>
                    <Button variant="default" size="sm" onClick={handleUpdateCompanyWebsite} disabled={updateCompanyProfileStatus === "pending" || !companyWebsite.trim()}>
                      {updateCompanyProfileStatus === "pending" ? <div className="spinner size-4 -ml-0.5" /> : null}
                      Save
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <Button variant="default" size="default" onClick={onUploadBrandGuidelines} disabled={uploadBrandGuidelinesStatus === "pending"} className="gap-2 w-full">
            {uploadBrandGuidelinesStatus === "pending" ? <div className="spinner size-4" /> : <CloudArrowUp className="size-5" />}
            {uploadBrandGuidelinesStatus === "pending" ? "Uploading..." : "Upload Brand Guidelines"}
          </Button>
        </div>
      </div>
    );
  }

  const colorsKey = company.brand_guidelines?.colors?.map((color: BrandColor) => color.hex).join(",") || "";

  return (
    <Fragment>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="flex flex-col gap-6 col-span-1 md:col-span-3">
          <BrandName name={company.company_name!} url={company.company_website!} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 min-h-48">
            <BrandLogo brandLogo={company?.brand_logo} status={updateCompanyProfileStatus} onUpdateCompanyProfile={handleUpdateCompanyProfile} />
            <BrandTypography typography={company.brand_guidelines?.typography || []} />
          </div>
          <BrandColors key={colorsKey} guidelines={company.brand_guidelines} status={updateCompanyProfileStatus} onUpdateCompanyProfile={handleUpdateCompanyProfile} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 min-h-48">
            <BrandVisualStandards guidelines={company.brand_guidelines} status={updateCompanyProfileStatus} onUpdateCompanyProfile={handleUpdateCompanyProfile} />
            <BrandVoice guidelines={company.brand_guidelines} status={updateCompanyProfileStatus} onUpdateCompanyProfile={handleUpdateCompanyProfile} />
          </div>
        </div>
      </div>
      {company.brand_research_status === "partial" ? (
        <div className="fixed bottom-10 left-1/2 -translate-x-1/2 z-40 flex flex-col items-center justify-center h-fit w-max md:w-fit pl-4 pr-5 py-3 bg-foreground text-background rounded-full border ring-2 ring-foreground/20">
          <div className="flex items-center justify-center gap-3">
            <div className="spinner size-4" />
            <p className="text-base">Your brand research is still in progress</p>
          </div>
        </div>
      ) : null}
    </Fragment>
  );
}

function BrandName({ name, url }: { name: string; url: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-3xl">{name}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2">
          <LinkIcon className="size-5" />
          <a href={url} target="_blank" rel="noopener noreferrer" className="text-sm hover:text-primary/80 transition-colors">
            {url}
          </a>
        </div>
      </CardContent>
    </Card>
  );
}

interface BrandLogoProps extends CompanyProfileProps {
  brandLogo: string | null | undefined;
}

function BrandLogo({ brandLogo, status, onUpdateCompanyProfile }: BrandLogoProps) {
  const logoInputRef = useRef<HTMLInputElement>(null);

  const { mutateAsync: uploadFile, status: uploadStatus } = useMutation({
    mutationFn: UploadApiFactory.Apis.UploadFile,
  });

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!mImageFormats.includes(file.type)) {
      toast.error("Unsupported image format", { description: mImageExtensions.join(", ") + " images are supported" });
      return;
    }

    try {
      const response = await uploadFile(file);
      const fileUrl = response.data.file_url;

      onUpdateCompanyProfile({ brand_logo: fileUrl });
      toast.success("Logo uploaded successfully!");
    } catch (error) {
      console.error("Failed to upload logo:", error);
      toast.error(unwrapApiError(error));
    } finally {
      event.target.value = "";
    }
  };

  const handleLogoClick = () => {
    logoInputRef.current?.click();
  };

  return (
    <Card className="col-span-1">
      <CardHeader>
        <CardTitle>Logo</CardTitle>
        <CardAction onClick={handleLogoClick}>
          <Button variant="ghost" size="icon" disabled={!brandLogo}>
            <PencilSimpleLine className="size-5" />
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="flex flex-col items-center justify-center h-full pb-3">
        {!brandLogo ? (
          <button
            onClick={handleLogoClick}
            disabled={uploadStatus === "pending" || status === "pending"}
            className="flex flex-col items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="size-16 rounded-full bg-secondary/60 flex items-center justify-center group-hover:bg-secondary transition-colors">
              {uploadStatus === "pending" || status === "pending" ? <div className="spinner size-8" /> : <Plus className="size-8" />}
            </div>
            <span className="text-sm font-medium">{uploadStatus === "pending" || status === "pending" ? "Uploading..." : "Add a logo"}</span>
          </button>
        ) : (
          <div className="flex items-center justify-center overflow-hidden rounded-full disabled:opacity-50 disabled:cursor-not-allowed">
            {uploadStatus === "pending" ? <div className="spinner size-8" /> : <img src={brandLogo} alt="Logo" className="w-full max-w-44 h-auto aspect-square rounded-full object-contain" />}
          </div>
        )}
        <input ref={logoInputRef} type="file" accept={mImageFormats.join(",")} className="hidden" onChange={handleLogoUpload} />
      </CardContent>
    </Card>
  );
}

function BrandTypography({ typography }: { typography: BrandTypography[] }) {
  return (
    <Card className="col-span-1 md:col-span-2">
      <CardHeader>
        <CardTitle className="text-lg">Typography</CardTitle>
        <CardAction className="hidden">
          <Button variant="ghost" size="icon" disabled>
            <PencilSimpleLine className="size-5" />
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="h-full">
        {typography && typography.length > 0 ? (
          <div className={cn("grid place-items-center place-content-center gap-4 mt-auto mx-auto h-full", typography.length === 1 ? "grid-cols-1" : "grid-cols-2")}>
            {typography.map((font, index) => (
              <div key={index} className="text-center">
                <div className="text-6xl text-muted-foreground" style={{ fontFamily: font.family || "inherit" }}>
                  Aa
                </div>
                <p className="text-sm text-foreground mt-2">{font.name}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-muted-foreground">No typography information available</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface BrandColorsProps extends CompanyProfileProps {
  guidelines: BrandDNA;
}

function BrandColors({ guidelines, status, onUpdateCompanyProfile }: BrandColorsProps) {
  const [isEditingColors, setEditingColors] = useState(false);
  const [isColorPickerOpen, setColorPickerOpen] = useState(false);

  const [colors, setColors] = useState(guidelines.colors || []);
  const [editingColorIndex, setEditingColorIndex] = useState<number | null>(null);
  const [selectedColorValue, setSelectedColorValue] = useState<ColorValue>(null);

  const handleColorClick = (hex: string, name?: string, index?: number) => {
    if (isEditingColors && index !== undefined) {
      setEditingColorIndex(index);
      setSelectedColorValue(hex);
      setColorPickerOpen(true);
    } else {
      window.navigator.clipboard.writeText(hex);
      toast.success(`${name || hex} copied to clipboard`);
    }
  };

  const handleStartEditColors = () => {
    setEditingColors(true);
    setColors([...(guidelines.colors || [])]);
  };

  const handleCancelEditColors = () => {
    setEditingColors(false);
    setColors(guidelines.colors || []);
    setEditingColorIndex(null);
  };

  const handleApplyColor = () => {
    if (!selectedColorValue || typeof selectedColorValue !== "string") {
      return;
    }

    if (editingColorIndex === null) {
      setColors((prev) => [...prev, { hex: selectedColorValue, name: null }]);
    } else {
      setColors((prev) => prev.map((color, index) => (index === editingColorIndex ? { ...color, hex: selectedColorValue } : color)));
    }

    setColorPickerOpen(false);
    setEditingColorIndex(null);
  };

  const handleSaveColors = async () => {
    try {
      onUpdateCompanyProfile({ brand_guidelines: { ...guidelines, colors } });

      setEditingColors(false);
      toast.success("Colors updated successfully!");
    } catch (error) {
      console.error("Failed to update colors:", error);
      toast.error(unwrapApiError(error));
    }
  };

  const handleRemoveColor = (event: React.MouseEvent<HTMLButtonElement>, index: number) => {
    event.stopPropagation();
    event.preventDefault();
    setColors((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddColor = () => {
    setEditingColorIndex(null);
    setSelectedColorValue("#000000");
    setColorPickerOpen(true);
  };

  const handleColorChange = (colorValue: ColorValue) => {
    setSelectedColorValue(colorValue);
  };

  const handleColorPickerClose = () => {
    if (!selectedColorValue || typeof selectedColorValue !== "string") {
      setColorPickerOpen(false);
      setEditingColorIndex(null);
      return;
    }

    if (editingColorIndex === null) {
      setColors((prev) => [
        ...prev,
        {
          hex: selectedColorValue,
          name: null,
        },
      ]);
    } else {
      const updatedColors = [...colors];
      if (editingColorIndex >= 0 && editingColorIndex < updatedColors.length) {
        updatedColors[editingColorIndex] = {
          ...updatedColors[editingColorIndex],
          hex: selectedColorValue,
        };
        setColors(updatedColors);
      }
    }

    setColorPickerOpen(false);
    setEditingColorIndex(null);
  };

  return (
    <Fragment>
      <Card className="min-h-48">
        <CardHeader>
          <CardTitle className="text-lg">Color Palette</CardTitle>
          <CardAction>
            {isEditingColors ? (
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={handleCancelEditColors}>
                  <X className="size-5" />
                </Button>
                <Button variant="ghost" size="icon" onClick={handleSaveColors} disabled={status === "pending"}>
                  <Check className="size-5" />
                </Button>
              </div>
            ) : (
              <Button variant="ghost" size="icon" onClick={handleStartEditColors}>
                <PencilSimpleLine className="size-5" />
              </Button>
            )}
          </CardAction>
        </CardHeader>
        <CardContent>
          {colors.filter((color) => !!color.hex).length > 0 ? (
            <div className="flex items-center gap-4 overflow-x-auto scrollbar-hidden py-3 px-2">
              {colors
                .filter((color) => !!color.hex)
                .slice(0, 6)
                .map((color, index) => (
                  <div key={index} className="flex flex-col items-center justify-center gap-2 relative">
                    <div
                      onClick={() => handleColorClick(color.hex || "", color.name || "", index)}
                      className={cn("size-20 rounded-full cursor-pointer transition-all border", !isEditingColors ? "hover:ring-2 hover:ring-primary hover:ring-offset-2" : "")}
                      style={{ backgroundColor: color.hex! }}
                    />
                    <button
                      onClick={(e) => handleRemoveColor(e, index)}
                      className={cn(
                        "absolute top-0 right-0 size-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center hover:bg-destructive/90 transition-colors",
                        !isEditingColors ? "hidden" : "flex"
                      )}
                    >
                      <X className="size-3" weight="bold" />
                    </button>
                    <span className="text-xs font-medium text-foreground text-center max-w-40 uppercase">{color.hex || "No color"}</span>
                  </div>
                ))}
              {isEditingColors ? (
                <button onClick={handleAddColor} className="flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-foreground transition-colors group">
                  <div className="size-20 rounded-full bg-background flex items-center justify-center group-hover:bg-secondary transition-colors border-2 border-dashed border-muted-foreground/50 group-hover:border-foreground/50">
                    <Plus className="size-8" weight="bold" />
                  </div>
                  <span className="text-xs font-medium">Add color</span>
                </button>
              ) : null}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full py-3">
              <p className="text-sm text-muted-foreground">No colors available</p>
            </div>
          )}
        </CardContent>
      </Card>
      <Drawer.Root direction="right" open={isColorPickerOpen} onOpenChange={setColorPickerOpen}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/40 dark:bg-black/80 backdrop-blur-xs z-50" />
          <Drawer.Content className="right-0 top-0 bottom-0 mt-8 w-full sm:max-w-md sm:mt-0 sm:right-2 sm:top-2 sm:bottom-2 fixed z-50 outline-none flex p-0" style={mDrawerStyle}>
            <Drawer.Title className="sr-only">Color Picker</Drawer.Title>
            <Drawer.Description className="sr-only">Select a color for your brand palette.</Drawer.Description>
            <FillComponent value={selectedColorValue} onChange={handleColorChange} onApply={handleApplyColor} onClose={handleColorPickerClose} brandColors={colors.map((color) => color.hex || "")} />
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </Fragment>
  );
}

interface BrandVisualStandardsProps extends CompanyProfileProps {
  guidelines: BrandDNA;
}

function BrandVisualStandards({ guidelines, status, onUpdateCompanyProfile }: BrandVisualStandardsProps) {
  const [isEditingVisualStandards, setIsEditingVisualStandards] = useState(false);
  const [visualStandardsValue, setVisualStandardsValue] = useState(guidelines.visual_layout_standards || "");

  const handleEditVisualStandards = () => {
    setIsEditingVisualStandards(true);
    setVisualStandardsValue(guidelines.visual_layout_standards || "");
  };

  const handleSaveVisualStandards = async () => {
    if (visualStandardsValue === guidelines.visual_layout_standards || visualStandardsValue.trim() === "") {
      setIsEditingVisualStandards(false);
      return;
    }

    try {
      onUpdateCompanyProfile({ brand_guidelines: { ...guidelines, visual_layout_standards: visualStandardsValue } });
      setIsEditingVisualStandards(false);
      toast.success("Visual standards updated successfully!");
    } catch (error) {
      console.error("Failed to update visual standards:", error);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Visual & Layout Standards</CardTitle>
        <CardAction>
          {isEditingVisualStandards ? (
            <Button variant="default" size="icon" onClick={handleSaveVisualStandards} disabled={status === "pending"}>
              <Check weight="bold" className="size-5" />
            </Button>
          ) : (
            <Button variant="ghost" size="icon" onClick={handleEditVisualStandards}>
              <PencilSimpleLine className="size-5" />
            </Button>
          )}
        </CardAction>
      </CardHeader>
      <CardContent className="h-full">
        {isEditingVisualStandards ? (
          <Textarea
            placeholder="Enter visual and layout standards..."
            autoFocus
            value={visualStandardsValue}
            onChange={(e) => setVisualStandardsValue(e.target.value)}
            className="h-full focus-visible:ring-0"
          />
        ) : guidelines.visual_layout_standards ? (
          <p className="text-sm text-muted-foreground">{guidelines.visual_layout_standards}</p>
        ) : (
          <div className="flex items-center justify-center h-full">
            <Button variant="secondary" size="default" onClick={handleEditVisualStandards}>
              <Plus className="size-4" />
              Add visual standards
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface BrandVoiceProps extends CompanyProfileProps {
  guidelines: BrandDNA;
}

function BrandVoice({ guidelines, status, onUpdateCompanyProfile }: BrandVoiceProps) {
  const [isOpen, setOpen] = useState(false);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Brand Voice</CardTitle>
        <CardAction>
          <BrandVoiceModal open={isOpen} onOpenChange={setOpen} guidelines={guidelines} status={status} onUpdateCompanyProfile={onUpdateCompanyProfile}>
            <Button variant="ghost" size="icon">
              <PencilSimpleLine className="size-5" />
            </Button>
          </BrandVoiceModal>
        </CardAction>
      </CardHeader>
      <CardContent className="h-full">
        {guidelines.brand_voice ? (
          <div className="space-y-5 text-sm">
            {Object.entries(guidelines.brand_voice).map(([key, value]) => (
              <Fragment key={key}>
                {Array.isArray(value) ? (
                  <div>
                    <span className="font-medium text-foreground capitalize">{key.split("_").join(" ")}: </span>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {value.map((item, index) => (
                        <span key={index} className="px-3 py-1 rounded-full bg-secondary text-xs text-foreground">
                          {String(item)}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div>
                    <span className="font-medium text-foreground capitalize">{key.split("_").join(" ")}: </span>
                    <span className="text-muted-foreground">{String(value)}</span>
                  </div>
                )}
              </Fragment>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <Button variant="secondary" size="default">
              <Plus className="size-4" />
              Add brand voice
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
